import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, FileUp } from 'lucide-react';
import { featureOptions, workTypes } from '../data/content';
import { hasValidationErrors, validateOrderForm, type ValidationErrors } from '../lib/validation';
import type { OrderFormValues, PendingOrder, ServiceTier } from '../types/portfolio';
import { SectionHeading } from './SectionHeading';

const initialValues: OrderFormValues = {
  name: '',
  email: '',
  handle: '',
  workType: '',
  category: '',
  budget: '',
  deadline: '',
  description: '',
  priority: 'Normal',
  features: [],
  honeypot: '',
};

type OrderFormProps = {
  selectedTier: ServiceTier;
  onOrderReady: (order: PendingOrder) => void;
};

export function OrderForm({ selectedTier, onOrderReady }: OrderFormProps) {
  const startedAt = useRef(Date.now());
  const [values, setValues] = useState<OrderFormValues>(initialValues);
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isReadyForPayment, setIsReadyForPayment] = useState(false);

  useEffect(() => {
    setValues((current) => ({
      ...current,
      workType: selectedTier.name,
      category: selectedTier.name,
      budget: selectedTier.price,
    }));
    setIsReadyForPayment(false);
  }, [selectedTier]);

  const selectedFileNames = useMemo(() => files.map((file) => file.name).join(', '), [files]);

  const updateValue = (key: keyof OrderFormValues, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setIsReadyForPayment(false);
  };

  const toggleFeature = (feature: string) => {
    setValues((current) => ({
      ...current,
      features: current.features.includes(feature)
        ? current.features.filter((item) => item !== feature)
        : [...current.features, feature],
    }));
    setIsReadyForPayment(false);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateOrderForm(values, files, startedAt.current);
    setErrors(nextErrors);
    if (hasValidationErrors(nextErrors)) return;

    onOrderReady({
      values: {
        ...values,
        workType: values.workType || selectedTier.name,
        category: values.category || selectedTier.name,
        budget: values.budget || selectedTier.price,
      },
      files,
    });
    setIsReadyForPayment(true);
    document.querySelector('#checkout')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="section-band" id="order">
      <div className="container">
        <SectionHeading eyebrow="Order" title="Order work" />

        <form className="order-form reveal" onSubmit={handleSubmit}>
          <input
            className="hidden-field"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={values.honeypot}
            onChange={(event) => updateValue('honeypot', event.target.value)}
          />

          <div className="form-grid">
            <label>
              Name
              <input value={values.name} onChange={(event) => updateValue('name', event.target.value)} placeholder="Your name" />
              {errors.name ? <span className="field-error">{errors.name}</span> : null}
            </label>

            <label>
              Email
              <input value={values.email} onChange={(event) => updateValue('email', event.target.value)} placeholder="hello@example.com" />
              {errors.email ? <span className="field-error">{errors.email}</span> : null}
            </label>

            <label>
              Telegram / Discord
              <input value={values.handle} onChange={(event) => updateValue('handle', event.target.value)} placeholder="@handle or username#0001" />
              {errors.handle ? <span className="field-error">{errors.handle}</span> : null}
            </label>

            <label>
              Work Type
              <select value={values.workType} onChange={(event) => updateValue('workType', event.target.value)}>
                <option value="">Select type</option>
                {workTypes.map((type) => (
                  <option value={type} key={type}>
                    {type}
                  </option>
                ))}
              </select>
              {errors.workType ? <span className="field-error">{errors.workType}</span> : null}
            </label>

            <label>
              Project Category
              <select value={values.category} onChange={(event) => updateValue('category', event.target.value)}>
                {['Site', 'Program', 'Game', 'State'].map((category) => (
                  <option value={category} key={category}>
                    {category}
                  </option>
                ))}
              </select>
              {errors.category ? <span className="field-error">{errors.category}</span> : null}
            </label>

            <label>
              Budget
              <input value={values.budget} onChange={(event) => updateValue('budget', event.target.value)} placeholder="$500" />
              {errors.budget ? <span className="field-error">{errors.budget}</span> : null}
            </label>

            <label>
              Deadline
              <input type="date" value={values.deadline} onChange={(event) => updateValue('deadline', event.target.value)} />
              {errors.deadline ? <span className="field-error">{errors.deadline}</span> : null}
            </label>

            <label>
              Priority
              <select value={values.priority} onChange={(event) => updateValue('priority', event.target.value as OrderFormValues['priority'])}>
                <option value="Normal">Normal</option>
                <option value="High">High</option>
                <option value="Urgent">Urgent</option>
              </select>
            </label>
          </div>

          <label className="full-field">
            Detailed Description
            <textarea
              value={values.description}
              onChange={(event) => updateValue('description', event.target.value)}
              placeholder="Describe the task: what needs to be done, which pages/logic, deadline, references and desired outcome."
            />
            {errors.description ? <span className="field-error">{errors.description}</span> : null}
          </label>

          <div className="feature-panel">
            <p>Required Features</p>
            <div className="features-grid">
              {featureOptions.map((feature) => (
                <label className="check-tile" key={feature}>
                  <input type="checkbox" checked={values.features.includes(feature)} onChange={() => toggleFeature(feature)} />
                  <span>{feature}</span>
                </label>
              ))}
            </div>
            {errors.features ? <span className="field-error">{errors.features}</span> : null}
          </div>

          <div className="upload-row">
            <label className="upload-box">
              <FileUp size={20} />
              <span>{selectedFileNames || 'Files'}</span>
              <input
                type="file"
                multiple
                onChange={(event) => {
                  setFiles(Array.from(event.target.files || []));
                  setIsReadyForPayment(false);
                }}
              />
            </label>
            {errors.files ? <span className="field-error">{errors.files}</span> : null}
          </div>

          {errors.spam ? <div className="form-alert">{errors.spam}</div> : null}

          {isReadyForPayment ? (
            <div className="form-success">
              <CheckCircle2 size={18} />
              Application is ready. Now proceed to payment below.
            </div>
          ) : null}

          <button className="btn btn-primary submit-button" type="submit">
            <CheckCircle2 size={18} />
            Continue to Payment
          </button>
        </form>
      </div>
    </section>
  );
}
